import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/api/client';
import type { PaginationInfo } from './accounting';

// ─────────────────────────────────────────────────────────────
// Comptes tiers (comptabilité auxiliaire) — répertoire + réglage
// d'exigibilité (spec 2026-07-24-comptes-tiers-design.md §4).
// Endpoints (montés sous /api/accounting) :
//   POST  /auxiliaries                    création manuelle (tous types)
//   GET   /auxiliaries                    liste/recherche (code+label, type, actif)
//   GET   /auxiliaries/:id                détail
//   GET/PATCH /settings/auxiliary-required exigibilité du tiers (AUXILIAIRE_OBLIGATOIRE)
// ─────────────────────────────────────────────────────────────

export type AuxiliaryAccountType =
  | 'CLIENT'
  | 'SUPPLIER'
  | 'EMPLOYEE'
  | 'INSTITUTION'
  | 'DONOR'
  | 'ASSOCIATE'
  | 'OTHER';

/** Libellés français des types de tiers (pages Tiers/Balance auxiliaire). */
export const AUXILIARY_TYPE_LABELS: Record<AuxiliaryAccountType, string> = {
  CLIENT: 'Client',
  SUPPLIER: 'Fournisseur',
  EMPLOYEE: 'Salarié',
  INSTITUTION: 'Institution',
  DONOR: 'Bailleur de fonds',
  ASSOCIATE: 'Associé',
  OTHER: 'Divers',
};

export interface AuxiliaryAccount {
  id: string;
  code: string;
  type: AuxiliaryAccountType;
  label: string;
  defaultCollectif: string;
  entityType: string | null;
  entityId: string | null;
  isSystem: boolean;
  isActive: boolean;
}

export interface AuxiliaryListParams {
  type?: AuxiliaryAccountType | undefined;
  /**
   * Filtre MULTI-types (revue T3 tiers, Minor 2) : sérialisé en
   * `types=A,B` — filtre SQL côté serveur (un collectif 46x admet DEUX
   * types ; filtrer côté client sur une page de 20 produisait de faux
   * « Aucun tiers »).
   */
  types?: AuxiliaryAccountType[] | undefined;
  isActive?: boolean | undefined;
  search?: string | undefined;
  page?: number | undefined;
  limit?: number | undefined;
}

export interface AuxiliariesResponse {
  auxiliaries: AuxiliaryAccount[];
  pagination: PaginationInfo;
}

export interface AuxiliaryRequiredSettings {
  auxiliaryRequired: boolean;
}

/**
 * Plafond AN_TIERS_MAX (comptes tiers, spec 2026-07-24 §5.6) : nombre
 * maximal de lignes d'à-nouveaux NOMMÉES par tiers, par compte
 * collectif, à la clôture — au-delà, regroupement « divers ».
 */
export interface AnTiersMaxSettings {
  anTiersMax: number;
}

export const auxiliaryKeys = {
  all: ['accounting', 'auxiliaries'] as const,
  list: (params: AuxiliaryListParams) => [...auxiliaryKeys.all, 'list', params] as const,
  detail: (id: string) => [...auxiliaryKeys.all, 'detail', id] as const,
  requiredSetting: () => [...auxiliaryKeys.all, 'auxiliary-required'] as const,
  anTiersMaxSetting: () => [...auxiliaryKeys.all, 'an-tiers-max'] as const,
};

async function fetchAuxiliaries(params: AuxiliaryListParams): Promise<AuxiliariesResponse> {
  const query = new URLSearchParams();
  if (params.type) query.set('type', params.type);
  if (params.types && params.types.length > 0) query.set('types', params.types.join(','));
  if (params.isActive !== undefined) query.set('isActive', String(params.isActive));
  if (params.search) query.set('search', params.search);
  if (params.page) query.set('page', String(params.page));
  if (params.limit) query.set('limit', String(params.limit));
  const suffix = query.toString() ? `?${query.toString()}` : '';
  return apiClient.get<AuxiliariesResponse>(`/accounting/auxiliaries${suffix}`);
}

/** Liste/recherche du répertoire des tiers (accounting:read). */
export function useAuxiliaries(
  params: AuxiliaryListParams = {},
  options?: { enabled?: boolean }
) {
  return useQuery({
    queryKey: auxiliaryKeys.list(params),
    queryFn: () => fetchAuxiliaries(params),
    enabled: options?.enabled ?? true,
  });
}

export interface CreateAuxiliaryInput {
  type: AuxiliaryAccountType;
  label: string;
  /**
   * Collectif par défaut. Optionnel : omis, le serveur le résout depuis
   * le mapping de rôle du tenant (et échoue bruyamment en 422 pour un
   * bailleur tant que DONOR_COLLECTIF n'est pas mappé).
   */
  defaultCollectif?: string;
}

/**
 * Création MANUELLE d'un tiers (accounting:write) — seul chemin pour les
 * types sans entité métier adossable (institution, bailleur, associé,
 * divers). Invalide la liste : le tiers créé apparaît aussitôt au
 * répertoire.
 */
export function useCreateAuxiliary() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateAuxiliaryInput) => {
      const result = await apiClient.post<{ auxiliary: AuxiliaryAccount }>(
        '/accounting/auxiliaries',
        input
      );
      return result.auxiliary;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: auxiliaryKeys.all });
    },
  });
}

/** Détail d'un compte tiers — résout le libellé d'un tiers déjà rattaché. */
export function useAuxiliary(id: string, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: auxiliaryKeys.detail(id),
    queryFn: () =>
      apiClient.get<{ auxiliary: AuxiliaryAccount }>(`/accounting/auxiliaries/${id}`),
    enabled: (options?.enabled ?? true) && id.length > 0,
  });
}

/**
 * Exigibilité du tiers (AUXILIAIRE_OBLIGATOIRE — pattern chartDigits) :
 * GET /api/accounting/settings/auxiliary-required (accounting:read).
 */
export function useAuxiliaryRequiredSettings(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: auxiliaryKeys.requiredSetting(),
    queryFn: () =>
      apiClient.get<AuxiliaryRequiredSettings>('/accounting/settings/auxiliary-required'),
    enabled: options?.enabled ?? true,
  });
}

/** PATCH /api/accounting/settings/auxiliary-required (accounting:settings). */
export function useSetAuxiliaryRequired() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (enabled: boolean) =>
      apiClient.patch<AuxiliaryRequiredSettings>('/accounting/settings/auxiliary-required', {
        enabled,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: auxiliaryKeys.requiredSetting() });
    },
  });
}

/**
 * Plafond AN_TIERS_MAX (spec 2026-07-24 §5.6, pattern auxiliary-required) :
 * GET /api/accounting/settings/an-tiers-max (accounting:read).
 */
export function useAnTiersMaxSettings(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: auxiliaryKeys.anTiersMaxSetting(),
    queryFn: () =>
      apiClient.get<AnTiersMaxSettings>('/accounting/settings/an-tiers-max'),
    enabled: options?.enabled ?? true,
  });
}

/** PATCH /api/accounting/settings/an-tiers-max (accounting:settings). */
export function useSetAnTiersMax() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (anTiersMax: number) =>
      apiClient.patch<AnTiersMaxSettings>('/accounting/settings/an-tiers-max', {
        anTiersMax,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: auxiliaryKeys.anTiersMaxSetting() });
    },
  });
}

// ─────────────────────────────────────────────────────────────
// Restitutions de tiers (Task 4 — spec §5.1-5.3) :
//   GET /auxiliaries/balance        balance auxiliaire (+ csv/pdf)
//   GET /auxiliaries/:id/ledger     grand livre du tiers
//   GET /auxiliaries/:id/releve     relevé de compte (json/pdf)
// ─────────────────────────────────────────────────────────────

export interface AuxiliaryBalanceRow {
  /** null = ligne « (sans tiers) ». */
  auxiliaryAccountId: string | null;
  code: string | null;
  label: string;
  type: string | null;
  debit: number;
  credit: number;
  solde: number;
}

export interface AuxiliaryBalanceCollectif {
  accountNumber: string;
  accountLabel: string;
  types: string[];
  familyLabel: string;
  rows: AuxiliaryBalanceRow[];
  /** Côté auxiliaire (tiers + « (sans tiers) »). */
  totals: { debit: number; credit: number; solde: number };
  /** Côté grand livre — le contrôle §1.2, calculé indépendamment. */
  generalLedger: { debit: number; credit: number; solde: number };
  /** generalLedger.solde − totals.solde — toujours exposé. */
  ecart: number;
  /** A1 — mouvements du collectif sans tiers : le contrôle qui varie. */
  sansTiers: number;
}

export interface AuxiliaryBalanceResponse {
  exercice: number | null;
  from: string | null;
  to: string | null;
  type: AuxiliaryAccountType | null;
  collectifs: AuxiliaryBalanceCollectif[];
  totals: {
    debit: number;
    credit: number;
    solde: number;
    generalLedgerSolde: number;
    ecart: number;
    /** A1 — Σ des mouvements sans tiers. */
    sansTiers: number;
  };
  /** Without payroll:read: the personnel collectifs carry no employee row, their totals stay. */
  employeeDetailWithheld?: boolean;
}

export interface AuxiliaryBalanceParams {
  exercice?: number;
  type?: AuxiliaryAccountType;
}

export interface AuxiliaryLedgerLine {
  id: string;
  date: string;
  journalCode: string;
  entryId: string;
  entryNumber: string | null;
  entryLabel: string;
  accountNumber: string;
  accountLabel: string;
  label: string | null;
  reference: string | null;
  debit: number;
  credit: number;
  /** Solde progressif (débits − crédits cumulés). */
  balance: number;
}

export interface AuxiliaryLedgerResponse {
  auxiliary: AuxiliaryAccount;
  lines: AuxiliaryLedgerLine[];
  totals: { totalDebit: number; totalCredit: number; balance: number };
}

export const auxiliaryReportKeys = {
  balance: (params: AuxiliaryBalanceParams) =>
    [...auxiliaryKeys.all, 'balance', params] as const,
  ledger: (id: string, collectif: string | undefined) =>
    [...auxiliaryKeys.all, 'ledger', id, collectif ?? null] as const,
};

function balanceSearch(params: AuxiliaryBalanceParams): string {
  const query = new URLSearchParams();
  if (params.exercice !== undefined) query.set('exercice', String(params.exercice));
  if (params.type) query.set('type', params.type);
  return query.toString() ? `?${query.toString()}` : '';
}

/** Balance auxiliaire (accounting:read) — écart §1.2 inclus. */
export function useAuxiliaryBalance(
  params: AuxiliaryBalanceParams = {},
  options?: { enabled?: boolean }
) {
  return useQuery({
    queryKey: auxiliaryReportKeys.balance(params),
    queryFn: () =>
      apiClient.get<AuxiliaryBalanceResponse>(
        `/accounting/auxiliaries/balance${balanceSearch(params)}`
      ),
    enabled: options?.enabled ?? true,
  });
}

/** Grand livre d'un tiers (accounting:read) — id-carried + legacy. */
export function useAuxiliaryLedger(
  id: string,
  params: { collectif?: string } = {},
  options?: { enabled?: boolean }
) {
  const suffix = params.collectif
    ? `?collectif=${encodeURIComponent(params.collectif)}`
    : '';
  return useQuery({
    queryKey: auxiliaryReportKeys.ledger(id, params.collectif),
    queryFn: () =>
      apiClient.get<AuxiliaryLedgerResponse>(
        `/accounting/auxiliaries/${id}/ledger${suffix}`
      ),
    enabled: (options?.enabled ?? true) && id.length > 0,
  });
}

/** Relevé de compte tiers en PDF (releve-tiers-CL0001.pdf). */
export async function downloadReleveTiersPdf(id: string, code: string): Promise<void> {
  await apiClient.download(
    `/accounting/auxiliaries/${id}/releve?format=pdf`,
    `releve-tiers-${code}.pdf`
  );
}

/** Balance auxiliaire en CSV (en-têtes français, écart inclus). */
export async function downloadAuxiliaryBalanceCsv(
  params: AuxiliaryBalanceParams
): Promise<void> {
  const search = balanceSearch(params);
  const separator = search ? '&' : '?';
  await apiClient.download(
    `/accounting/auxiliaries/balance${search}${separator}format=csv`,
    `balance-auxiliaire-${params.exercice ?? 'periode'}.csv`
  );
}

/** Balance auxiliaire en PDF (contrôle grand livre + écart dessinés). */
export async function downloadAuxiliaryBalancePdf(
  params: AuxiliaryBalanceParams
): Promise<void> {
  const search = balanceSearch(params);
  const separator = search ? '&' : '?';
  await apiClient.download(
    `/accounting/auxiliaries/balance${search}${separator}format=pdf`,
    `balance-auxiliaire-${params.exercice ?? 'periode'}.pdf`
  );
}
